<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  normalizeBands,
  toggleScoring as computeToggledScoring,
} from '../helpers/scoring';

const props = defineProps({
  stages: { type: Array, default: () => [] },
  maxPoints: { type: Number, default: 0 },
});

const scoring = defineModel('scoring', { type: Object, default: () => ({}) });

const { t } = useI18n();

const TEMPERATURES = ['hot', 'warm', 'cold', 'frozen'];

// A barra de cobertura fala pela cor: a ordem quente > morno > frio > gelado
// tem que ser lida sem legenda, então segue o mesmo vocabulário de cor que o
// resto do app usa para temperatura de lead.
const TEMPERATURE_BG = {
  hot: 'bg-n-ruby-9',
  warm: 'bg-n-amber-9',
  cold: 'bg-n-blue-9',
  frozen: 'bg-n-slate-9',
};

const temperatureLabel = temp =>
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  t(`FORMS_MGMT.EDITOR.SCORING.TEMPERATURES.${temp.toUpperCase()}`);

const isEnabled = computed(() => !!scoring.value?.enabled);
const bands = computed(() => scoring.value?.bands || []);

const maxPointsLabel = computed(
  () => `${t('FORMS_MGMT.EDITOR.SCORING.MAX_POINTS')}: ${props.maxPoints}`
);

// O max="100"/min="0" do <input type="number"> é só advisório — o navegador
// não impede realmente digitar/colar um valor fora do intervalo. Sem o
// clamp aqui, um max negativo persiste (normalizeBands() só força a ÚLTIMA
// faixa, pós-ordenação, a fechar em 100 — não toca no piso).
const clampMax = value => Math.min(100, Math.max(0, Number(value)));

// Rascunho do que está sendo digitado, SÓ para o texto da faixa acompanhar a
// tecla. O estado de verdade continua saindo no @change — se a reordenação
// respondesse ao @input, a linha trocaria embaixo do cursor (ver a regressão
// de digitação no spec).
const drafts = ref({});

const bandMax = index => {
  const draft = drafts.value[index];
  return clampMax(draft === undefined ? bands.value[index]?.max : draft);
};

// O campo editável é só o TETO da faixa; onde ela COMEÇA é sempre o teto da
// anterior + 1 (Forms::ScoringService#band_for usa `score <= max`, com as
// faixas ordenadas). Sem mostrar isso, "a nota 50 cai em qual faixa?" não tem
// resposta na tela — foi exatamente a dúvida que originou este bloco.
const bandFrom = index => (index === 0 ? 0 : bandMax(index - 1) + 1);

// O <input> precisa exibir o RASCUNHO enquanto ele existe. Ligado direto em
// `band.max`, cada re-render disparado pela digitação reescrevia o campo com
// o valor antigo, e o @change acabava lendo esse valor antigo de volta.
const bandInput = index => drafts.value[index] ?? bands.value[index]?.max;

// Quantas notas a faixa cobre, contando as duas pontas: a de 0 a 49 vale por
// 50 notas. É esse número que vira a largura do trecho na barra — uma faixa
// "20 a 20" cobre 1 e aparece como o risco fino que de fato é.
const bandSpan = index => Math.max(0, bandMax(index) - bandFrom(index) + 1);

const coverageTotal = computed(() =>
  bands.value.reduce((total, _band, index) => total + bandSpan(index), 0)
);

const coverageWidth = index => {
  const total = coverageTotal.value;
  return total ? `${(bandSpan(index) / total) * 100}%` : '0%';
};

// Duas configurações deixam a pontuação ligada e sem efeito nenhum, sem erro
// nenhum: nenhuma pergunta com peso (ScoringService devolve nil quando o
// máximo é zero) e nenhum funil escolhido (sem funil não há mapping, então
// LeadIngestionService não move etapa nem grava temperatura).
const hasNoPoints = computed(() => isEnabled.value && props.maxPoints === 0);
const hasNoFunnel = computed(() => isEnabled.value && !props.stages.length);

// normalizeBands() (helpers/scoring.js) reordena o array pelo max.
//
// CUIDADO: o input de "max" no template escuta @change (só dispara ao perder
// o foco/confirmar), NUNCA @input — se disparasse a cada tecla, o valor
// intermediário podia cruzar o limite de uma faixa vizinha e reordenar a
// lista NO MEIO da digitação. Como o v-for das faixas é chaveado por índice,
// o <input> focado seria reaproveitado para outra faixa e o usuário
// continuaria digitando dentro do registro errado — uma faixa fica com a
// configuração corrompida sem nenhum erro visível.
const emitScoring = patch => {
  scoring.value = { ...scoring.value, ...patch };
};

const toggleScoring = enabled => {
  scoring.value = computeToggledScoring(scoring.value, enabled);
};

const updateBand = (index, key, value) => {
  const updated = bands.value.map((band, i) =>
    i === index
      ? { ...band, [key]: key === 'max' ? clampMax(value) : value }
      : band
  );
  // Os rascunhos morrem aqui: normalizeBands() pode reordenar, e um rascunho
  // guardado por índice passaria a descrever a faixa errada.
  drafts.value = {};
  emitScoring({ bands: normalizeBands(updated) });
};

const draftMax = (index, value) => {
  drafts.value = { ...drafts.value, [index]: value };
};

const addBand = () => {
  emitScoring({
    bands: normalizeBands([
      ...bands.value,
      {
        max: 100,
        stage: '',
        label: '',
        temperature: 'warm',
        disqualified: false,
      },
    ]),
  });
};

const removeBand = index => {
  emitScoring({
    bands: normalizeBands(bands.value.filter((_, i) => i !== index)),
  });
};

defineExpose({ toggleScoring, updateBand, addBand, removeBand });
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between">
      <div>
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.SCORING.TITLE') }}
        </span>
        <p class="text-xs text-n-slate-11">
          {{ t('FORMS_MGMT.EDITOR.SCORING.DESCRIPTION') }}
        </p>
      </div>
      <input
        :checked="isEnabled"
        type="checkbox"
        class="form-checkbox"
        :aria-label="t('FORMS_MGMT.EDITOR.SCORING.TITLE')"
        @change="toggleScoring($event.target.checked)"
      />
    </div>

    <div
      v-if="isEnabled"
      data-testid="scoring-bands"
      class="flex flex-col gap-2"
    >
      <p class="text-xs text-n-slate-11">
        {{ t('FORMS_MGMT.EDITOR.SCORING.SCORE_HELP') }}
      </p>
      <p class="text-xs text-n-slate-11">
        {{ maxPointsLabel }}
      </p>

      <p
        v-if="hasNoPoints"
        data-testid="warning-no-points"
        class="rounded-lg border border-n-amber-5 bg-n-amber-3 px-3 py-2 text-xs text-n-amber-12"
      >
        {{ t('FORMS_MGMT.EDITOR.SCORING.WARNING_NO_POINTS') }}
      </p>
      <p
        v-if="hasNoFunnel"
        data-testid="warning-no-funnel"
        class="rounded-lg border border-n-amber-5 bg-n-amber-3 px-3 py-2 text-xs text-n-amber-12"
      >
        {{ t('FORMS_MGMT.EDITOR.SCORING.WARNING_NO_FUNNEL') }}
      </p>

      <!-- Barra de cobertura: as faixas em escala sobre o eixo 0-100. Uma
           faixa degenerada (largura 1) vira um risco fino, e uma temperatura
           fora de ordem vira uma cor fora de ordem — os dois erros que
           passavam batidos lendo só os números. -->
      <div class="flex flex-col gap-1 pt-1">
        <div
          data-testid="coverage-bar"
          class="flex h-2 overflow-hidden rounded-full bg-n-alpha-2"
        >
          <span
            v-for="(band, index) in bands"
            :key="index"
            data-testid="coverage-segment"
            :data-span="bandSpan(index)"
            :data-temperature="band.temperature"
            :style="{ width: coverageWidth(index) }"
            :class="TEMPERATURE_BG[band.temperature] || 'bg-n-slate-9'"
            :title="`${bandFrom(index)}–${bandMax(index)} · ${band.label}`"
          />
        </div>
        <div class="flex text-[10px] text-n-slate-11">
          <span
            v-for="(band, index) in bands"
            :key="index"
            :style="{ width: coverageWidth(index) }"
            class="truncate ltr:pr-1 rtl:pl-1"
          >
            {{ band.label }}
          </span>
        </div>
      </div>

      <div
        data-testid="bands-header"
        class="hidden gap-2 text-xs font-medium text-n-slate-11 sm:grid sm:grid-cols-[9rem_minmax(0,1fr)_minmax(0,8rem)_7rem_6rem_1.75rem] sm:items-center"
      >
        <span>{{ t('FORMS_MGMT.EDITOR.SCORING.BAND_RANGE') }}</span>
        <span>{{ t('FORMS_MGMT.EDITOR.SCORING.BAND_STAGE') }}</span>
        <span>{{ t('FORMS_MGMT.EDITOR.SCORING.BAND_LABEL') }}</span>
        <span>{{ t('FORMS_MGMT.EDITOR.SCORING.BAND_TEMPERATURE') }}</span>
        <span>{{ t('FORMS_MGMT.EDITOR.SCORING.BAND_DISQUALIFIED') }}</span>
        <span />
      </div>

      <!-- Em coluna estreita cada campo vira uma linha COM rótulo (o `sm:hidden`
           dos labels), em vez de empilhar mudo como acontecia antes. -->
      <div
        v-for="(band, index) in bands"
        :key="index"
        class="grid grid-cols-1 gap-2 border-n-weak py-2 [&:not(:last-child)]:border-b sm:grid-cols-[9rem_minmax(0,1fr)_minmax(0,8rem)_7rem_6rem_1.75rem] sm:items-center sm:border-none sm:py-0"
      >
        <div class="flex items-center gap-1.5">
          <span class="text-xs text-n-slate-11">
            {{ t('FORMS_MGMT.EDITOR.SCORING.BAND_RANGE_FROM') }}
          </span>
          <span
            data-testid="band-from"
            class="text-sm tabular-nums text-n-slate-12"
          >
            {{ bandFrom(index) }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('FORMS_MGMT.EDITOR.SCORING.BAND_RANGE_TO') }}
          </span>
          <input
            :value="bandInput(index)"
            type="number"
            min="0"
            max="100"
            class="!mb-0 !h-9 !w-[4.5rem] tabular-nums"
            :disabled="index === bands.length - 1"
            :aria-label="t('FORMS_MGMT.EDITOR.SCORING.BAND_MAX')"
            @input="draftMax(index, $event.target.value)"
            @change="updateBand(index, 'max', $event.target.value)"
          />
        </div>
        <label class="flex flex-col gap-1 sm:contents">
          <span class="text-xs text-n-slate-11 sm:hidden">
            {{ t('FORMS_MGMT.EDITOR.SCORING.BAND_STAGE') }}
          </span>
          <select
            :value="band.stage"
            class="!mb-0 !h-9"
            :aria-label="t('FORMS_MGMT.EDITOR.SCORING.BAND_STAGE')"
            @change="updateBand(index, 'stage', $event.target.value)"
          >
            <option value="">
              {{ t('FORMS_MGMT.EDITOR.SCORING.BAND_STAGE_EMPTY') }}
            </option>
            <option
              v-for="stage in props.stages"
              :key="stage.name"
              :value="stage.name"
            >
              {{ stage.label || stage.name }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1 sm:contents">
          <span class="text-xs text-n-slate-11 sm:hidden">
            {{ t('FORMS_MGMT.EDITOR.SCORING.BAND_LABEL') }}
          </span>
          <input
            :value="band.label"
            type="text"
            class="!mb-0 !h-9"
            :placeholder="t('FORMS_MGMT.EDITOR.SCORING.BAND_LABEL')"
            :aria-label="t('FORMS_MGMT.EDITOR.SCORING.BAND_LABEL')"
            @input="updateBand(index, 'label', $event.target.value)"
          />
        </label>
        <label class="flex flex-col gap-1 sm:contents">
          <span class="text-xs text-n-slate-11 sm:hidden">
            {{ t('FORMS_MGMT.EDITOR.SCORING.BAND_TEMPERATURE') }}
          </span>
          <select
            :value="band.temperature"
            class="!mb-0 !h-9"
            :aria-label="t('FORMS_MGMT.EDITOR.SCORING.BAND_TEMPERATURE')"
            @change="updateBand(index, 'temperature', $event.target.value)"
          >
            <option v-for="temp in TEMPERATURES" :key="temp" :value="temp">
              {{ temperatureLabel(temp) }}
            </option>
          </select>
        </label>
        <label class="flex items-center gap-2 text-xs text-n-slate-11">
          <input
            type="checkbox"
            class="h-4 w-4 accent-n-brand"
            :checked="band.disqualified === true"
            @change="updateBand(index, 'disqualified', $event.target.checked)"
          />
          <span class="sm:hidden">
            {{ t('FORMS_MGMT.EDITOR.SCORING.BAND_DISQUALIFIED') }}
          </span>
        </label>
        <Button
          icon="i-lucide-x"
          ruby
          xs
          faded
          class="justify-self-start"
          :disabled="bands.length <= 1"
          @click="removeBand(index)"
        />
      </div>

      <Button
        icon="i-lucide-plus"
        slate
        xs
        faded
        class="w-fit"
        :label="t('FORMS_MGMT.EDITOR.SCORING.BAND_ADD')"
        @click="addBand"
      />
    </div>
  </div>
</template>
